import { useSyncExternalStore, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, ConfirmDialog, useToast } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useShiftStore } from '@/stores/use-shift-store';
import { useRegisterStore } from '@/stores/use-register-store';
import { useNetworkStore } from '@/stores/use-network-store';
import { useSyncStore } from '@/stores/use-sync-store';
import { LocationSwitcher } from './LocationSwitcher';
import { formatElapsed } from '@/lib/shift-helpers';
import { appUrl } from '@/lib/app-url';
import {
  getSocketStatus,
  subscribeSocketStatus,
  type SocketStatus,
} from '@/lib/socket-client';

function useSocketStatus(): SocketStatus {
  return useSyncExternalStore(subscribeSocketStatus, getSocketStatus, getSocketStatus);
}

export function AppStatusBar() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const profile = useAuthStore((s) => s.profile);
  const logout = useAuthStore((s) => s.logout);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // T2-012: the "Logout anyway?" text, shown in an in-app dialog (never window.confirm).
  const [logoutConfirm, setLogoutConfirm] = useState<string | null>(null);
  const activeShift = useShiftStore((s) => s.activeShift);
  const elapsedMinutes = useShiftStore((s) => s.elapsedMinutes);
  const registerSession = useRegisterStore((s) => s.session);
  const hasRegisters = useRegisterStore((s) => s.registers.length > 0);
  const isConnected = useNetworkStore((s) => s.isConnected);
  const pendingCount = useSyncStore((s) => s.pendingCount);
  // T19: this till's bills that failed or need review.
  const attentionCount = useSyncStore((s) => s.attentionCount);
  const socketStatus = useSocketStatus();

  // T17 (owner, Odoo rule): logout wipes this device, so it waits
  // for every offline bill — pending, failed, dead or in conflict.
  const warnUnsyncedBills = (count: number) =>
    toast({
      title: 'Sync pending bills first',
      description: `${count} offline item(s) are not on the server yet — waiting, failed or awaiting a manager. Open Sync to send them; logout stays blocked until then.`,
      variant: 'warning',
    });

  // gst-parked 120: parks made offline upload to the store's list before logout.
  const warnUnuploadedParks = (count: number) =>
    toast({
      title: 'Upload parked bills first',
      description: `${count} parked bill(s) on this till are not on the server yet. Go back online so they upload, or open Parked Bills to resume or discard them; logout stays blocked until then.`,
      variant: 'warning',
    });

  /** True when logout was refused for work that would be lost (T17 family). */
  const warnIfUnsynced = (u: { pending_sync?: number; parked_bills?: number } | undefined) => {
    if ((u?.pending_sync ?? 0) > 0) {
      warnUnsyncedBills(u?.pending_sync ?? 0);
      return true;
    }
    if ((u?.parked_bills ?? 0) > 0) {
      warnUnuploadedParks(u?.parked_bills ?? 0);
      return true;
    }
    return false;
  };

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      const forced = await logout({ force: true });
      setLogoutConfirm(null);
      if (forced.blocked) {
        warnIfUnsynced(forced.unsynced);
        return;
      }
      navigate('/login', { replace: true });
    } catch (err) {
      setLogoutConfirm(null);
      toast({
        title: err instanceof Error ? err.message : 'Logout failed',
        variant: 'error',
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const userName = profile?.name ?? profile?.email ?? null;
  const userRole = profile?.role ?? null;

  // Hide the Live pill entirely when we're offline — the Offline indicator
  // already tells the user nothing real-time will arrive.
  const showLivePill = isConnected && socketStatus !== 'idle';
  const isLive = socketStatus === 'connected';
  const isReconnecting = socketStatus === 'reconnecting' || socketStatus === 'connecting';

  const isReadOnly = profile?.tenant?.is_read_only === true;

  return (
    <div className="shrink-0">
      {isReadOnly && (
        <div
          role="alert"
          aria-label="Account suspended — read-only mode"
          className="flex items-center justify-center gap-2 px-4 py-1 bg-warning-600 text-white text-xs font-medium"
        >
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          Account suspended — read-only mode. Contact support to restore access.
        </div>
      )}
    <div className="flex items-center gap-3 px-4 py-1.5 bg-neutral-900 dark:bg-neutral-950 text-xs text-neutral-400">
      {/* Network status */}
      <div className="flex items-center gap-1.5">
        <span
          className={`inline-block w-2 h-2 rounded-full ${
            isConnected
              ? 'bg-success-500'
              : 'bg-danger-500 animate-pulse'
          }`}
        />
        <span className={isConnected ? 'text-neutral-400' : 'text-danger-400'}>
          {isConnected ? 'Online' : 'Offline'}
        </span>
      </div>

      {/* Realtime (WebSocket) status — hidden when offline */}
      {showLivePill && (
        <div
          className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/5 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-label={isLive ? 'Realtime connected' : 'Realtime reconnecting'}
        >
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${
              isLive ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'
            }`}
          />
          <span
            className={`tabular-nums text-[11px] ${
              isLive ? 'text-emerald-300' : 'text-amber-300'
            }`}
          >
            {isLive ? 'Live' : isReconnecting ? 'Reconnecting' : 'Offline'}
          </span>
        </div>
      )}

      {/* Pending sync count */}
      {pendingCount > 0 && (
        <button
          type="button"
          onClick={() => navigate('/pos/sync')}
          className="flex items-center gap-1 hover:text-neutral-200 transition-colors"
          aria-label={`${pendingCount} pending sync transactions`}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <Badge variant="warning" size="sm">
            {pendingCount}
          </Badge>
        </button>
      )}

      {/* T19: offline bills that failed or need review */}
      {attentionCount > 0 && (
        <button
          type="button"
          onClick={() => navigate('/pos/sync')}
          className="flex items-center gap-1 text-danger-400 hover:text-danger-300 transition-colors"
          aria-label={`${attentionCount} offline bill${attentionCount === 1 ? '' : 's'} failed or need review`}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <Badge variant="danger" size="sm">
            {attentionCount}
          </Badge>
        </button>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Active shift */}
      {activeShift ? (
        <button
          type="button"
          onClick={() => navigate('/pos/shift/close')}
          className="flex items-center gap-1 text-success-400 hover:text-success-300 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>{activeShift.name}</span>
          <span className="tabular-nums">{formatElapsed(elapsedMinutes)}</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/pos/shift/open')}
          className="flex items-center gap-1 text-warning-400 hover:text-warning-300 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>No Shift</span>
        </button>
      )}

      {/* Register session (T12) — shown once the store has registers */}
      {(registerSession || hasRegisters) && (
        <button
          type="button"
          onClick={() => navigate('/pos/register')}
          className={
            registerSession
              ? 'flex items-center gap-1 text-success-400 hover:text-success-300 transition-colors'
              : 'flex items-center gap-1 text-warning-400 hover:text-warning-300 transition-colors'
          }
        >
          <span className="text-neutral-500">|</span>
          <span>
            {registerSession
              ? `${registerSession.register_name} · ${registerSession.session_number.split('-').pop()}`
              : 'Register closed'}
          </span>
        </button>
      )}

      {/* Location switcher — lets users with 0/1/2+ locations pick their outlet */}
      {userName && (
        <div className="flex items-center gap-1.5">
          <span className="text-neutral-500">|</span>
          <LocationSwitcher />
        </div>
      )}

      {/* User info */}
      {userName && (
        <div className="flex items-center gap-1.5">
          <span className="text-neutral-500">|</span>
          <span className="text-neutral-300 truncate max-w-[120px]">
            {userName}
          </span>
          {userRole && (
            <span className="text-neutral-500">{userRole}</span>
          )}
        </div>
      )}

      {/* Dashboard — OWNER / MANAGER only; opens the admin app */}
      {(userRole === 'OWNER' || userRole === 'MANAGER') && (
        <a
          href={appUrl('admin', '/dashboard')}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-neutral-400 hover:text-brand-300 transition-colors"
          aria-label="Open business dashboard"
          title="Open business dashboard"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span>Dashboard</span>
        </a>
      )}

      {/* Logout */}
      {userName && (
        <button
          type="button"
          disabled={isLoggingOut}
          onClick={async () => {
            if (isLoggingOut) return;
            setIsLoggingOut(true);
            try {
              const result = await logout();
              if (result.blocked) {
                const u = result.unsynced;
                if (warnIfUnsynced(u)) return;
                const lines: string[] = [];
                if (u?.open_shift) {
                  lines.push(
                    'You have an active shift that has not been clocked out. The shift will remain open on the server until you clock out.',
                  );
                }
                if ((u?.draft_bills ?? 0) > 0) {
                  lines.push(
                    `${u?.draft_bills} unfinished cart(s) on this device will be discarded.`,
                  );
                }
                if ((u?.local_quotes ?? 0) > 0) {
                  lines.push(
                    `${u?.local_quotes} offline quotation(s) not yet on the server will be discarded.`,
                  );
                }
                setLogoutConfirm(lines.join(' '));
              } else {
                navigate('/login', { replace: true });
              }
            } catch (err) {
              toast({
                title: err instanceof Error ? err.message : 'Logout failed',
                variant: 'error',
              });
            } finally {
              setIsLoggingOut(false);
            }
          }}
          className="flex items-center gap-1 text-neutral-400 hover:text-danger-300 transition-colors disabled:opacity-50"
          aria-label="Logout"
          title="Logout"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>{isLoggingOut ? 'Logging out…' : 'Logout'}</span>
        </button>
      )}
      <ConfirmDialog
        open={logoutConfirm !== null}
        title="Logout anyway?"
        description={logoutConfirm ?? ''}
        confirmLabel="Logout"
        cancelLabel="Cancel"
        variant="warning"
        onConfirm={() => { void confirmLogout(); }}
        onCancel={() => setLogoutConfirm(null)}
        loading={isLoggingOut}
      />
    </div>
    </div>
  );
}
